import type { BillBreakdown } from '@/calc/tariff';
import type { FanComparison } from '@/calc/fans';
import { formatKwh, formatLkr } from '@/lib/format';

interface Row {
  label: string;
  normal: string;
  atomberg: string;
  emphasize?: boolean;
  muted?: boolean;
}

export function ComparisonTable({
  fans,
  normalBill,
  atombergBill,
}: {
  fans: FanComparison;
  normalBill: BillBreakdown;
  atombergBill: BillBreakdown;
}) {
  const rows: Row[] = [
    { label: 'Fan units (this house, per month)', normal: formatKwh(fans.normalUnitsPerMonth), atomberg: formatKwh(fans.atombergUnitsPerMonth) },
    { label: 'Total monthly usage', normal: formatKwh(normalBill.units), atomberg: formatKwh(atombergBill.units) },
    { label: 'Tariff category', normal: normalBill.category.label, atomberg: atombergBill.category.label },
    { label: 'Energy charge', normal: formatLkr(normalBill.energyLkr), atomberg: formatLkr(atombergBill.energyLkr), muted: true },
    { label: 'Fixed charge', normal: formatLkr(normalBill.fixedChargeLkr), atomberg: formatLkr(atombergBill.fixedChargeLkr), muted: true },
    {
      label: `SSCL tax (${normalBill.ssclRatePercent}%)`,
      normal: formatLkr(normalBill.ssclLkr),
      atomberg: formatLkr(atombergBill.ssclLkr),
      muted: true,
    },
    {
      label: 'Total monthly bill',
      normal: formatLkr(normalBill.totalLkr),
      atomberg: formatLkr(atombergBill.totalLkr),
      emphasize: true,
    },
  ];

  return (
    <>
      {/* Mobile: stacked cards */}
      <div className="sm:hidden space-y-2">
        {rows.map((r) => (
          <div
            key={r.label}
            className={`rounded-lg border p-3 ${
              r.emphasize ? 'border-brand-500 bg-brand-50' : 'border-slate-200 bg-white'
            } ${r.muted ? 'text-slate-500' : ''}`}
          >
            <div className={`text-xs uppercase tracking-wide mb-2 ${r.emphasize ? 'text-slate-800' : 'text-slate-500'}`}>
              {r.label}
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <div className="text-xs text-slate-500 mb-0.5">Normal</div>
                <div className="tabular-nums text-slate-900 font-medium">{r.normal}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-0.5">Atomberg</div>
                <div className="tabular-nums text-slate-900 font-medium">{r.atomberg}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tablet+: table */}
      <div className="hidden sm:block overflow-hidden rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-2 font-medium"></th>
              <th className="text-right px-4 py-2 font-medium">Normal fans</th>
              <th className="text-right px-4 py-2 font-medium text-slate-800">Atomberg fans</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.label}
                className={`${r.emphasize ? 'bg-brand-50 font-semibold' : ''} ${r.muted ? 'text-slate-500' : ''}`}
              >
                <td className="px-4 py-2">{r.label}</td>
                <td className="px-4 py-2 text-right tabular-nums">{r.normal}</td>
                <td className="px-4 py-2 text-right tabular-nums text-slate-900">
                  {r.atomberg}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
